import { Pipe, PipeTransform } from '@angular/core';
import { DateTime, Duration } from 'luxon';
import { DefaultTimeFormate } from 'planning-portal-library/src/lib/data/shared/api-constants/default-time-formate';

@Pipe({
  name: 'luxonDate',
  pure: true, // Improves performance
  standalone: true,
})
export class LuxonDatePipe implements PipeTransform {
  transform(
    value: DateTime | Duration | Date | string | undefined,
    format = 'HH:mm' // Default format
  ): string {
    if (!value) return '00:00'; // Handle undefined/null

    let dateTimeToUse: DateTime | null = null;

    if (typeof value === 'string') {
      const trimmedValue = value.trim(); // Remove extra spaces

      // Handle ISO 8601 Duration like "PT8H505M"
      if (trimmedValue.startsWith('PT')) {
        const duration = Duration.fromISO(trimmedValue);
        if (!duration.isValid) return '00:00';

        // Convert extra minutes into hours properly
        const { hours, minutes } = duration
          .shiftTo('hours', 'minutes')
          .toObject();
        const validHours = (hours || 0) + Math.floor((minutes || 0) / 60);
        const validMinutes = (minutes || 0) % 60;

        return `${String(validHours).padStart(2, '0')}:${String(
          validMinutes
        ).padStart(2, '0')}`;
      }

      // Handle "HH:mm:ss" or "HH:mm" formats
      if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmedValue)) {
        dateTimeToUse = DateTime.fromFormat(
          trimmedValue,
          trimmedValue.length === 5 ? 'HH:mm' : 'HH:mm:ss'
        );
      } else {
        dateTimeToUse = DateTime.fromISO(trimmedValue, { setZone: true });
      }
    } else if (value instanceof Date) {
      dateTimeToUse = DateTime.fromJSDate(value);
    } else if (value instanceof Duration) {
      // Convert extra minutes into hours properly
      const { hours, minutes, seconds } = value
        .shiftTo('hours', 'minutes', 'seconds')
        .toObject();

      // Valid hours: total hours + leftover hours from minutes
      const validHours = (hours || 0) + Math.floor((minutes || 0) / 60);
      const validMinutes = (minutes || 0) % 60;
      const validSeconds = Math.floor(seconds || 0); // Ensure seconds is an integer

      // Return formatted string in hh:mm:ss
      return `${String(validHours).padStart(2, '0')}:${String(
        validMinutes
      ).padStart(2, '0')}`;
    } else {
      dateTimeToUse = value;
    }

    return dateTimeToUse?.isValid ? dateTimeToUse.toFormat(format) : '00:00';
  }
}
